/**
 * LotStatutModal — quarantine or release a lot (POST /inventory/lots/:id/statut, cap
 * inventory.mouvement.create). A motif (≥ 2 chars, médico-légal) is mandatory. Reversible + audited.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Button, FormField, Modal, Textarea, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { INV_CAPS } from "../caps";
import { useLabId, useSetLotStatut, inventoryKeys } from "../api";
import { useQueryClient } from "@tanstack/react-query";

export interface LotStatutModalProps {
  open: boolean;
  lotId: string | null;
  numeroLot?: string;
  /** Target statut: "quarantaine" to hold, "actif" to release. */
  target: "quarantaine" | "actif";
  /** The reagent owning the lot — for targeted cache invalidation of its risk/reorder. */
  reactifId?: string | null;
  onClose: () => void;
  onDone: () => void;
}

export function LotStatutModal({
  open,
  lotId,
  numeroLot,
  target,
  reactifId,
  onClose,
  onDone,
}: LotStatutModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const qc = useQueryClient();
  const canSet = useCan(INV_CAPS.mouvementCreate);
  const mutation = useSetLotStatut(labId);

  const [motif, setMotif] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMotif("");
    setTouched(false);
  }, [open, lotId]);

  const motifError = touched && motif.trim().length < 2 ? t("inventory.field.required") : null;
  const valid = motif.trim().length >= 2;
  const isQuarantine = target === "quarantaine";

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canSet || !lotId) return;
    mutation.mutate(
      { lotId, statut: target, motif: motif.trim() },
      {
        onSuccess: () => {
          toast.success(t(isQuarantine ? "inventory.lots.quarantine.success" : "inventory.lots.release.success"));
          // Refresh this reagent's risk view so the chip flips immediately.
          if (reactifId) {
            void qc.invalidateQueries({ queryKey: inventoryKeys.risk(labId, reactifId) });
            void qc.invalidateQueries({ queryKey: inventoryKeys.reorder(labId, reactifId) });
          }
          onDone();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${t(isQuarantine ? "inventory.lots.quarantine.title" : "inventory.lots.release.title")}${
        numeroLot ? ` — ${numeroLot}` : ""
      }`}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("inventory.action.cancel")}
          </Button>
          <Button
            type="submit"
            form="inventory-lot-statut"
            variant={isQuarantine ? "danger" : "primary"}
            loading={mutation.isPending}
            disabled={!canSet || !valid}
          >
            {t(isQuarantine ? "inventory.lots.quarantine.submit" : "inventory.lots.release.submit")}
          </Button>
        </>
      }
    >
      <form id="inventory-lot-statut" onSubmit={submit} noValidate>
        <FormField
          label={t("inventory.lots.statut.motif")}
          hint={t("inventory.lots.statut.motif.hint")}
          required
          {...(motifError ? { error: motifError } : {})}
        >
          {(p) => (
            <Textarea
              {...p}
              autoFocus
              rows={3}
              value={motif}
              invalid={!!motifError}
              onChange={(e) => setMotif(e.target.value)}
            />
          )}
        </FormField>
      </form>
    </Modal>
  );
}
